/**
 * Google Ads / Google Analytics 4 (gtag.js) yardımcı fonksiyonları
 * GA4 Measurement ID: G-XXXXXXXXXX
 * Google Ads ID: AW-XXXXXXXXXX
 */

declare global {
  interface Window {
    gtag: (command: 'config' | 'event' | 'js', targetId: string, config?: Record<string, unknown>) => void;
    dataLayer: unknown[];
  }
}

const GTAG_SCRIPT_URL = 'https://www.googletagmanager.com/gtag/js';

/**
 * gtag script'ini sayfaya ekler (id ile bir kez yüklenir)
 */
export function loadGtagScript(): Promise<void> {
  if (document.querySelector(`script[src^="${GTAG_SCRIPT_URL}"]`)) {
    return Promise.resolve();
  }
  return new Promise(resolve => {
    window.dataLayer = window.dataLayer || [];
    const script = document.createElement('script');
    script.async = true;
    script.src = `${GTAG_SCRIPT_URL}?id=${getMeasurementId()}`;
    script.onload = () => resolve();
    document.head.appendChild(script);
  });
}

function getMeasurementId(): string {
  return import.meta.env.VITE_GA_MEASUREMENT_ID || import.meta.env.VITE_GOOGLE_ADS_ID || 'G-';
}

/**
 * Google tag'i başlatır (GA4 ve/veya Google Ads)
 */
export function gtagInit(): void {
  if (typeof window.gtag !== 'function') {
    // eslint-disable-next-line prefer-rest-params -- gtag stub must push arguments to dataLayer
    window.gtag = function () {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(arguments as unknown as Record<string, unknown>);
    };
  }

  const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  const adsId = import.meta.env.VITE_GOOGLE_ADS_ID;

  if (gaId) {
    window.gtag('config', gaId, {
      send_page_view: false,
      anonymize_ip: true,
    });
  }
  if (adsId) {
    window.gtag('config', adsId, {
      send_page_view: false,
    });
  }
}

/**
 * Sayfa görüntüleme olayı gönderir
 */
export function gtagPageView(path: string, title?: string): void {
  if (typeof window.gtag !== 'function') return;
  const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (gaId) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
      send_to: gaId,
    });
  }
}

/**
 * Özel olay gönderir (form gönderimi, tıklama vb.)
 */
export function gtagEvent(eventName: string, params?: Record<string, string | number | boolean>): void {
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, params);
}

/**
 * Google Ads dönüşüm (conversion) olayı gönderir
 * @param conversionLabel - Google Ads'de tanımlı conversion label (örn. "AbCdEfGhIjKlMnOpQ")
 */
export function gtagConversion(conversionLabel: string, value?: number, currency?: string): void {
  const adsId = import.meta.env.VITE_GOOGLE_ADS_ID;
  if (!adsId || typeof window.gtag !== 'function') return;
  const sendTo = `${adsId}/${conversionLabel}`;
  window.gtag('event', 'conversion', {
    send_to: sendTo,
    value,
    currency: currency || 'TRY',
  });
}

export const isGoogleAdsEnabled = (): boolean =>
  !!(import.meta.env.VITE_GA_MEASUREMENT_ID || import.meta.env.VITE_GOOGLE_ADS_ID);
